import React, { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Control, useFieldArray, useWatch } from 'react-hook-form';
import { twJoin, twMerge } from 'tailwind-merge';

import { CloseIcon } from '../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../components/button/index';
import { FormInput } from '../../../../../../components/input/index';
import Loader from '../../../../../../components/loader/index';
import { ScenarioHooks, useDebounce } from '../../../../../../hooks/index';
import {
  CreateScenarioPool,
  DilutionImpact,
} from '../../../../../../types/scenario-modeling.types';
import { removeSpaces } from '../../../../../../utils/remove-spaces.util';
import { toNumber } from '../../../../../../utils/to-number.util';
import { useScenarioCapTableStore } from '../../../../cap-table.store';
import { useScenarioStore } from '../../../../scenario-modeling.store';
import { FundraisingFormSchema, poolsFormSchema } from '../../../fundraising-validation';
import { DilutionImpactCombobox } from '../form-components/dilution-impact-combobox';
import { TableHeaderItem } from '../form-components/index';
import { ShareClassCombobox } from '../form-components/share-class/combobox';
import { NewItemListProps } from './items.types';

export type PoolItemProps = {
  companyId: string;
  control: Control<FundraisingFormSchema>;
  index: number;
  onRemoveItem: () => void;
  scenarioId: string;
  setIsPending: (isPending: boolean) => void;
  skipUpdateIdsRef: React.MutableRefObject<Set<string>>;
};

export const PoolItem: FC<PoolItemProps> = ({
  control,
  index,
  companyId,
  scenarioId,
  onRemoveItem,
  skipUpdateIdsRef,
  setIsPending,
}) => {
  const { pools } = useWatch<FundraisingFormSchema>({ control });
  const { success: poolsSuccess } = poolsFormSchema.safeParse(pools);
  const poolItem = pools?.poolItems?.[index];
  const lastSentRef = useRef<CreateScenarioPool | null>(null);

  const { createPool, isPending } = ScenarioHooks.useCreateScenarioPool();
  const { updateScenarioPool, isPending: isUpdatePending } = ScenarioHooks.usePatchScenarioPool();
  const { deleteScenarioPool } = ScenarioHooks.useDeleteScenarioPool();

  const { pointerBlockedStyles } = useScenarioStore();

  const { scheduleUpdate } = useScenarioCapTableStore();

  const handleRemoveItem = () => {
    if (poolItem?.id) {
      scheduleUpdate(scenarioId);
      skipUpdateIdsRef.current.add(poolItem.id);
      return deleteScenarioPool(
        { poolId: poolItem.id, scenarioId },
        {
          onSuccess: () => {
            onRemoveItem();
            lastSentRef.current = null;

            setTimeout(() => {
              if (poolItem?.id) {
                skipUpdateIdsRef.current.delete(poolItem.id);
              }
            }, 1000);
          },
        },
      );
    } else {
      onRemoveItem();
      lastSentRef.current = null;
    }
  };

  const extractPoolSnapshot = useCallback(
    (item: typeof poolItem) => ({
      dilutedShares: toNumber(item?.dilutedShares),
      name: removeSpaces(item?.name)?.trim(),
      shareClassId: item?.shareClass?.id || '',
      type: item?.type,
    }),
    [],
  );

  const { debounceCallback } = useDebounce<typeof poolItem>({
    debounce: 500,
    callback: (item) => {
      if (!item || !poolsSuccess) return;
      if (item.id && skipUpdateIdsRef.current.has(item.id)) return;

      const poolSnapshot = extractPoolSnapshot(item);
      const { dilutedShares, id, name, shareClass, type } = item;

      if (JSON.stringify(poolSnapshot) === JSON.stringify(lastSentRef.current)) return;

      if (id) {
        scheduleUpdate(scenarioId);
        updateScenarioPool({
          poolId: id,
          scenarioId,
          data: {
            dilutedShares: toNumber(dilutedShares),
            name: removeSpaces(name)?.trim(),
            shareClassId: shareClass?.id || '',
            type,
          },
        });
      } else {
        scheduleUpdate(scenarioId);
        createPool({
          scenarioId,
          data: {
            dilutedShares: toNumber(dilutedShares),
            name: removeSpaces(name)?.trim(),
            shareClassId: shareClass?.id || '',
            type,
          },
        });
      }
      lastSentRef.current = poolSnapshot;
    },
  });

  useEffect(() => {
    if (!poolItem || (poolItem?.id && skipUpdateIdsRef.current.has(poolItem?.id))) return;

    const currentSnapshot = extractPoolSnapshot(poolItem);
    if (lastSentRef.current === null) {
      lastSentRef.current = currentSnapshot;
      return;
    }

    if (JSON.stringify(currentSnapshot) === JSON.stringify(lastSentRef.current)) return;

    debounceCallback(poolItem);
  }, [poolItem, debounceCallback, skipUpdateIdsRef, extractPoolSnapshot]);

  useEffect(() => {
    if (isPending || isUpdatePending) {
      setIsPending(true);
    } else {
      const timeout = setTimeout(() => {
        setIsPending(false);
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [isPending, isUpdatePending, setIsPending]);

  return (
    <>
      <div className={twJoin('px-2 py-4', pointerBlockedStyles)}>
        <FormInput
          control={control}
          inputClassName="h-10"
          name={`pools.poolItems.${index}.name`}
          transform={(value) => removeSpaces(typeof value === 'string' ? value : undefined)}
          wrapperClassName="w-full"
        />
      </div>
      <div className={twJoin('px-2 py-4', pointerBlockedStyles)}>
        <FormInput
          control={control}
          inputClassName="h-10"
          name={`pools.poolItems.${index}.dilutedShares`}
          numberOnly
          shouldFormatNumber
          wrapperClassName="w-full"
        />
      </div>
      <div className={twJoin('flex items-center px-2 py-4', pointerBlockedStyles)}>
        <ShareClassCombobox
          companyId={companyId}
          control={control}
          name={`pools.poolItems.${index}.shareClass`}
        />
      </div>
      <div className={twMerge('flex flex-col gap-2 px-2 py-4', pointerBlockedStyles)}>
        <DilutionImpactCombobox control={control} index={index} />
      </div>
      <div className={twMerge('flex h-full items-center justify-center', pointerBlockedStyles)}>
        <Button
          className="w flex h-full w-full items-center justify-center"
          onClick={handleRemoveItem}
          styleType="NONE"
        >
          <CloseIcon />
        </Button>
      </div>
    </>
  );
};

export const PoolItemList: FC<NewItemListProps> = ({
  control,
  companyId,
  columns,
  addButtonText,
  scenarioId,
}) => {
  const { pools } = useWatch<FundraisingFormSchema>({ control });
  const { success: poolsSuccess } = poolsFormSchema.safeParse(pools);
  const skipUpdateIdsRef = useRef<Set<string>>(new Set());
  const [isPending, setIsPending] = useState<boolean>(false);

  const {
    fields: itemsFields,
    append: itemsAppend,
    remove: itemsRemove,
  } = useFieldArray({
    control,
    name: 'pools.poolItems',
  });

  const handleRemoveItems = useCallback(
    (id: string) => {
      const index = itemsFields.findIndex((field) => field.id === id);
      if (index !== -1) {
        itemsRemove(index);
      }
    },
    [itemsFields, itemsRemove],
  );

  const handleAppendItems = useCallback(() => {
    itemsAppend({
      dilutedShares: 0,
      name: '',
      shareClass: { id: '', name: '' },
      type: DilutionImpact.PRE_ROUND,
    });
  }, [itemsAppend]);

  return (
    <div
      className={twMerge(
        'relative flex w-full flex-col gap-3',
        isPending ? 'pointer-events-none opacity-50' : '',
      )}
    >
      {isPending && <Loader className="absolute z-10" />}
      {itemsFields.length > 0 && (
        <div className="flex flex-col rounded-lg border-[1px] border-gray-100 bg-white">
          <div
            className="grid"
            style={{
              gridTemplateColumns: `160px 226px repeat(2, minmax(160px, 1fr)) 48px`,
            }}
          >
            {columns.map((column, index) => (
              <TableHeaderItem key={index} text={column} />
            ))}
            <TableHeaderItem text="" />
          </div>
          {itemsFields.map((field, index) => (
            <Fragment key={field.id}>
              <div
                className="grid items-start border-b-[1px] border-b-gray-100"
                key={field.id}
                style={{
                  gridTemplateColumns: '160px 226px repeat(2, minmax(160px, 1fr)) 48px',
                }}
              >
                <PoolItem
                  companyId={companyId}
                  control={control}
                  index={index}
                  onRemoveItem={() => handleRemoveItems(field.id)}
                  scenarioId={scenarioId}
                  setIsPending={setIsPending}
                  skipUpdateIdsRef={skipUpdateIdsRef}
                />
              </div>
            </Fragment>
          ))}
        </div>
      )}

      <div className="w-full bg-white">
        <Button
          className="w-fit px-4 py-2 text-sm font-[450] text-brand-700 underline disabled:border-transparent disabled:bg-transparent disabled:ring-0"
          disabled={itemsFields?.length > 0 && !poolsSuccess}
          onClick={handleAppendItems}
          styleType="NONE"
        >
          + <AppFormattedMessage id="ADD" /> {addButtonText}
        </Button>
      </div>
    </div>
  );
};
